import clsx from 'clsx';
import { getAuth } from 'firebase/auth';
import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from 'src/contexts/AuthContext/FirebaseAuthContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { useRightHandSidebar } from 'src/contexts/RightHandSidebarContext';
import { post } from 'src/api/requests';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { getPayloadJWT, isJWT } from 'src/utils/jwt';
import Cookies from 'universal-cookie';
import { PageTitle } from './PageTitle';
import RightHandSidebar from './RightHandSidebar';
import { Sidebar } from './Sidebar';

type LayoutProps = {
  children?: React.ReactNode;
};

const cookies = new Cookies(null, { path: '/' });
const DAYS_WARNING_THRESHOLD = 4;

export const LoggedLayout = ({ children }: LayoutProps) => {
  const { isOpen, sidebarWidth } = useRightHandSidebar();
  const defaultAuth = getAuth();
  const auth = useFirebaseAuth();
  const notification = useNotification();
  const [lockAt, setLockAt] = useState<Date>();
  const location = useLocation();
  const { get } = useAuthedRequest();
  const { serviceKey } = useServiceMode();
  cookies.set('userId', auth?.user?.uid);

  const isBannerOpen = () => !!lockAt;
  const daysLeft = lockAt
    ? Math.max(
        0,
        Math.floor(
          (lockAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
        ),
      )
    : 0;

  useEffect(() => {
    const user = auth?.user;
    if (user?.uid && !location.pathname.includes('/onboarding')) {
      window.pylon = {
        chat_settings: {
          app_id: process.env.REACT_APP_PYLON_APP_ID,
          email: user.email,
          name: user.displayName,
          avatar_url: user.photoURL,
        },
      };
    }
  }, [auth?.user, location.pathname]);

  const fetchCurrentUserAndOrganization = useCallback(async () => {
    const token = await defaultAuth.currentUser?.getIdToken();

    if (token && isJWT(token) && !serviceKey) {
      const decoded = getPayloadJWT(token);

      if (decoded.organization) {
        const organization = await get(
          `/organizations/${decoded.organization.id}`,
        );
        if (organization.lockAt) {
          setLockAt(new Date(organization.lockAt));
        }
      }
    }
  }, [defaultAuth, get, serviceKey]);

  useEffect(() => {
    if (!defaultAuth) {
      return;
    }

    fetchCurrentUserAndOrganization();
  }, [defaultAuth, fetchCurrentUserAndOrganization]);

  const resendVerificationEmail = useCallback(async () => {
    if (auth?.user) {
      try {
        await post('/users/me/verification-email', {});
        notification.success('Verification email sent');
      } catch {
        notification.error('Failed to send verification email');
      }
    }
  }, [auth, notification]);

  const calculateBannerOffsets = (numBanners: number) => {
    const BANNER_HEIGHT = 40;
    const BANNER_MARGIN = 48;

    return {
      heightOffset: numBanners * BANNER_HEIGHT,
      topMargin: numBanners * BANNER_MARGIN,
    };
  };

  return (
    <div className="flex flex-col bg-neutral-50">
      <PageTitle />
      {isBannerOpen() && (
        <div
          className={clsx(
            'text-neutral-0 text-center p-2 text-sm lg:text-base fixed top-0 w-full z-10',
            daysLeft <= DAYS_WARNING_THRESHOLD ? 'bg-warning' : 'bg-primary',
          )}
        >
          Welcome to Fluents.ai! You have <b>{daysLeft}</b> days left on your
          current plan
        </div>
      )}

      {!auth?.user?.emailVerified && (
        <div
          className={clsx(
            'text-neutral-0 text-center p-2 text-sm lg:text-base fixed w-full z-10 bg-primary-700',
            isBannerOpen() ? 'top-10' : 'top-0',
          )}
        >
          Please check your email to verify your account or click
          <a
            href="#"
            onClick={resendVerificationEmail}
            className="underline mx-1"
          >
            here
          </a>
          to resend the verification
        </div>
      )}

      <div
        className={clsx('flex m-2 bg-neutral-0 rounded-xl', 'min-h-[98vh]')}
        style={(() => {
          const numBanners = [
            isBannerOpen(),
            !auth?.user?.emailVerified,
          ].filter(Boolean).length;
          if (numBanners === 0) return {};
          const { heightOffset, topMargin } =
            calculateBannerOffsets(numBanners);
          return {
            minHeight: `calc(97vh - ${heightOffset}px)`,
            marginTop: `${topMargin}px`,
          };
        })()}
      >
        <Sidebar />

        <div className={clsx('w-full block border-l border-neutral-100')}>
          <div
            className={clsx(
              'grid w-full transition-all duration-300',
              isOpen ? `grid-cols-[1fr_${sidebarWidth}]` : 'grid-cols-[1fr_0]',
            )}
          >
            <div
              className={clsx(
                'transition-all duration-300 flex flex-1 overflow-y-auto',
              )}
            >
              <div
                className={clsx(
                  'bg-neutral-0 w-full',
                  !isOpen ? 'rounded-r-xl' : 'rounded-r-none',
                  'overflow-auto',
                )}
              >
                {children || <Outlet />}
              </div>
            </div>

            <RightHandSidebar />
          </div>
        </div>
      </div>
    </div>
  );
};
